import React, { useMemo } from 'react';
import { useSubscription } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DrawIcon from '@material-ui/icons/Gesture';
import { procedureFormSub } from '../graph/surgeon/forms';
import { FormSaveType } from '../types/Form';

/**
 * Helpers for `SignableUpload` forms: per-patient documents that staff upload here (view-web) and that the patient
 * reviews and signs in view-care. They share the upload storage with regular `Uploaded` forms but are rendered in
 * their own group with the patient's signing status, and only accept PDFs.
 */

export const isSignableUpload = form => form?.saveType === FormSaveType.SignableUpload;

/**
 * Form lists in view-web (Forms tab, charting forms, …) should not show signable uploads: they are managed from the
 * upload section, and their `content` is an icon name rather than a renderable form.
 */
export const withoutSignableUploads = forms => (forms || []).filter(form => !isSignableUpload(form));

export const splitSignableUploads = forms => ({
  regular: withoutSignableUploads(forms),
  signable: (forms || []).filter(isSignableUpload),
});

/**
 * Map of formId -> FormSigning (`isSigned`) for the signable-upload forms of a procedure. Live: the stream refreshes
 * when staff upload/remove a file (FormUpdated) and when the patient signs (SignatureUpdated).
 */
export const useSignableUploadSigning = (procedureId, signableForms) => {
  const { data } = useSubscription(procedureFormSub, {
    variables: { procedureId },
    skip: !procedureId || !signableForms || signableForms.length === 0,
  });

  return useMemo(() => {
    const byId = {};
    (data?.procedureFormSub || []).forEach(signing => {
      byId[signing.id] = signing;
    });
    return byId;
  }, [data]);
};

/**
 * Signing status for one signable-upload form.
 * `signing` is undefined until staff upload a document (the API only lists the form for signing once it has a file).
 */
export const SignableUploadStatus = ({ signing, hasFiles }) => {
  if (!hasFiles && !signing) {
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<DrawIcon />}
        label="Upload a PDF for the patient to sign"
        title="Once a PDF is uploaded here, the patient will see it in the Sign step of their questionnaire."
      />
    );
  }

  if (signing?.isSigned) {
    return (
      <Chip
        size="small"
        color="primary"
        icon={<CheckCircleIcon />}
        label="Signed by patient"
        title="The patient reviewed and signed this document. Replacing or removing the file will require a new signature."
      />
    );
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<HourglassEmptyIcon />}
      label="Awaiting patient signature"
      title="The patient will be asked to review and sign this document in their questionnaire."
    />
  );
};

export const SignableUploadGroupHeader = () => (
  <Box px={1} pt={2} pb={1} width="100%">
    <Typography variant="subtitle2">For patient review &amp; signature</Typography>
    <Typography variant="caption" color="textSecondary">
      Upload the PDF the patient has to sign. It is shown to the patient in the Sign step of the questionnaire, not in
      the Upload step.
    </Typography>
  </Box>
);
